// ============================================
// ThreeColumnLayout.jsx - GhostInk Layout
// ============================================
// Three-column layout with collapsible sidebars
// Left: Session Manager
// Center: Main Chat
// Right: Files/Preview Panel

import React, { useState, useCallback, useEffect } from 'react';
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from 'lucide-react';

// ============================================
// RESIZE HANDLE COMPONENT
// ============================================
function ResizeHandle({ side, onResize }) {
  const [isDragging, setIsDragging] = useState(false);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      onResize(e.clientX, side);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, onResize, side]);

  const visualPosition = side === 'left' ? 'right' : 'left';

  return (
    <div
      className={`resize-handle ${visualPosition} ${isDragging ? 'active' : ''}`}
      onMouseDown={handleMouseDown}
    />
  );
}

// ============================================
// SIDEBAR TOGGLE BUTTON
// ============================================
function SidebarToggle({ position, isOpen, onClick }) {
  const Icon = position === 'left'
    ? isOpen ? PanelLeftClose : PanelLeftOpen
    : isOpen ? PanelRightClose : PanelRightOpen;

  return (
    <button
      className={`sidebar-toggle ${position}`}
      onClick={onClick}
      title={isOpen ? `Close ${position} panel` : `Open ${position} panel`}
    >
      <Icon size={16} />
    </button>
  );
}

// ============================================
// MAIN THREE COLUMN LAYOUT
// ============================================
export default function ThreeColumnLayout({
  leftSidebar,
  mainContent,
  rightSidebar,
  leftOpen = true,
  rightOpen = false,
  onLeftToggle,
  onRightToggle,
  leftWidth: initialLeftWidth = 280,
  rightWidth: initialRightWidth = 360,
  onLeftWidthChange,
  onRightWidthChange,
  rightExpanded = false,
}) {
  const [leftWidth, setLeftWidth] = useState(initialLeftWidth);
  const [rightWidth, setRightWidth] = useState(initialRightWidth);

  // Handle resize
  const handleResize = useCallback((clientX, side) => {
    if (side === 'left') {
      const newWidth = Math.min(Math.max(clientX, 200), 400);
      setLeftWidth(newWidth);
      onLeftWidthChange?.(newWidth);
    } else if (side === 'right') {
      const windowWidth = window.innerWidth;
      const newWidth = Math.min(Math.max(windowWidth - clientX, 300), windowWidth * 0.6);
      setRightWidth(newWidth);
      onRightWidthChange?.(newWidth);
    }
  }, [onLeftWidthChange, onRightWidthChange]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const activeElement = document.activeElement;
      const isTyping =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        activeElement?.contentEditable === 'true';

      if (isTyping) return;

      // Cmd/Ctrl + B = Toggle left sidebar
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        onLeftToggle?.(!leftOpen);
      }
      // Cmd/Ctrl + . = Toggle right sidebar
      if ((e.metaKey || e.ctrlKey) && e.key === '.') {
        e.preventDefault();
        onRightToggle?.(!rightOpen);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [leftOpen, rightOpen, onLeftToggle, onRightToggle]);

  return (
    <div className="ghostink-app">
      {/* Left Sidebar */}
      <aside
        className={`gi-left-sidebar ${leftOpen ? '' : 'collapsed'}`}
        style={{ width: leftOpen ? leftWidth : 0 }}
      >
        {leftOpen && (
          <>
            {leftSidebar}
            <ResizeHandle side="left" onResize={handleResize} />
          </>
        )}
      </aside>

      {/* Toggle for closed left sidebar */}
      {!leftOpen && (
        <SidebarToggle
          position="left"
          isOpen={leftOpen}
          onClick={() => onLeftToggle?.(true)}
        />
      )}

      {/* Main Content */}
      {mainContent}

      {/* Toggle for closed right sidebar */}
      {!rightOpen && (
        <SidebarToggle
          position="right"
          isOpen={rightOpen}
          onClick={() => onRightToggle?.(true)}
        />
      )}

      {/* Right Sidebar */}
      <aside
        className={`gi-right-sidebar ${rightOpen ? '' : 'collapsed'} ${rightExpanded ? 'expanded' : ''}`}
        style={{ width: rightOpen ? (rightExpanded ? undefined : rightWidth) : 0 }}
      >
        {rightOpen && (
          <>
            <ResizeHandle side="right" onResize={handleResize} />
            {rightSidebar}
          </>
        )}
      </aside>
    </div>
  );
}
